import { Check } from "lucide-react";
import { Bundle } from "@/pages/bundles/types/bundle";

const ACCENT = "#EA5B34"; // brand orange (matches the public card)

// Read-only preview of the public pricing card built from the bundle's
// Card Display metadata (tagline / features / most-popular) + pricing.
export default function BundleCardPreview({ bundle }: { bundle: Bundle }) {
  const meta = (bundle.metadata ?? {}) as {
    tagline?: string | null;
    features?: string[];
    isMostPopular?: boolean;
  };
  const tagline = meta.tagline?.trim();
  const features = (meta.features ?? []).map((f) => f.trim()).filter(Boolean);
  const isMostPopular = !!meta.isMostPopular;

  const currency = bundle.currency;
  const amount = bundle.amount ?? 0;
  const sessions = bundle.session_quantity ?? 0;
  const hasSessions = sessions > 0;
  const perSession = hasSessions ? Math.round(amount / sessions) : amount;

  const money = (n: number) => `${currency} ${n.toLocaleString()}`;
  const heading = hasSessions
    ? `${sessions} Session${sessions > 1 ? "s" : ""}`
    : bundle.name;

  return (
    <div className="relative w-full max-w-xs rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      {isMostPopular && (
        <span
          className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-full px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white"
          style={{ backgroundColor: ACCENT }}
        >
          Most popular
        </span>
      )}

      <div className="text-center">
        <p className="text-sm font-bold text-gray-900">{heading}</p>
        <p className="mt-1 text-3xl font-extrabold tracking-tight text-gray-900">
          {money(perSession)}
        </p>
        {hasSessions && (
          <p className="mt-1 text-xs text-gray-400">per session</p>
        )}
      </div>

      {hasSessions && (
        <p className="mt-4 text-center text-sm text-gray-900">
          Billed as{" "}
          <span className="font-semibold" style={{ color: ACCENT }}>
            {money(amount)}
          </span>{" "}
          for {sessions} session{sessions > 1 ? "s" : ""}
        </p>
      )}

      {(tagline || features.length > 0) && (
        <div className="my-4 border-t border-gray-200" />
      )}

      {tagline && (
        <p className="text-center text-sm font-bold uppercase tracking-wide text-gray-900">
          {tagline}
        </p>
      )}

      {features.length > 0 && (
        <ul className="mt-3 space-y-2">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2">
              <span
                className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: ACCENT }}
              >
                <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
              </span>
              <span className="text-xs text-gray-600">{f}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        disabled
        className="mt-5 w-full cursor-default rounded-lg border border-gray-200 py-2 text-center text-sm font-bold text-gray-900"
      >
        Continue
      </button>
    </div>
  );
}
